// Copyright (c) StrangeBee 2023

import { TaskLogController, TaskLogInput, TaskLogOutput } from 'thehive-sdk';

export interface TaskLogEventList {
    onList: (caseId: string) => Promise<TaskLogOutput[]>;
    onCreate: (taskLog: TaskLogInput, taskId: string) => Promise<any>;
    onEdit: (taskLog: Partial<TaskLogInput>, taskId: string) => void;
    onMultiEdit: (taskLog: Partial<TaskLogInput>, taskId: string) => Promise<any>;
    onDelete: (taskLogId: string) => void;
    onCreateAttachments: (attachments: File[], taskLogId: string) => void;
    onDeleteAttachments: (logId: string, objectId: string) => void;
}

interface TaskLogEventListParams {
    locale: (label: string) => string;
    controller: TaskLogController;
    onSuccess: (count?: boolean, message?: string) => void;
    onError: (label?: string) => void;
}

export const computeEventList = ({
    controller,
    locale,
    onError,
    onSuccess,
}: TaskLogEventListParams): TaskLogEventList => ({
    onList: (caseId: string): Promise<TaskLogOutput[]> =>
        controller.list<TaskLogOutput[]>({
            selector: { head: [{ name: 'getCase', idOrName: caseId }, { name: 'tasks' }], name: 'get-all-tasks' },
        }),
    onCreate: (taskLog: TaskLogInput, taskId: string): Promise<any> => {
        return controller
            .create(taskLog, taskId)
            .then(() => onSuccess(true, locale('notification.task-log.create')))
            .catch(onError);
    },
    onEdit: (taskLog, taskLogId): void => {
        controller
            .edit(taskLog, taskLogId)
            .then(() => onSuccess?.(false, locale('notification.task-log.create')))
            .catch(onError);
    },
    onMultiEdit: (taskLog, taskLogId): Promise<any> => {
        return controller
            .multiEdit(taskLog, taskLogId)
            .then(() => onSuccess?.(false, locale('notification.task-log.edit')))
            .catch(onError);
    },
    onDelete: (taskLogId): void => {
        controller
            .delete(taskLogId)
            .then(() => onSuccess?.(true, locale('notification.task-log.delete')))
            .catch(onError);
    },
    onCreateAttachments: (attachments: File[], taskLogId: string): void => {
        controller
            .createAttachments(attachments, taskLogId)
            .then(() => onSuccess?.(false, locale('notification.task-log.create')))
            .catch(onError);
    },
    onDeleteAttachments: (logId: string, objectId: string): void => {
        controller
            .deleteAttachments(logId, objectId)
            .then(() => onSuccess?.(false, locale('notification.task-log.create')))
            .catch(onError);
    },
});
